import React, { useState, useEffect, useCallback } from "react";
import { 
    Drawer, 
    Descriptions, 
    Badge, 
    Tag, 
    Button, 
    Space, 
    Divider, 
    Table, 
    Form, 
    Select, 
    InputNumber, 
    Popconfirm, 
    message, 
    Card, 
    FloatButton,
    Spin,
    Alert,
    Typography
} from "antd";
import { EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { contractManagementService } from "../../../../api/enhancedApi";
import { formatDateTime } from "../../../../utils/dateUtils";
import { useAuth } from "../../../../hooks/useAuth";
import PermissionGuard from "../../../../components/auth/PermissionGuard";
import { SERVICE_TYPE_MAP } from "../../SupplierContract/constants";

const { Option } = Select;
const { Title, Text } = Typography;

const ProductBundleDetailDrawer = ({ visible, onClose, bundle, onEdit, onSuccess }) => {
    const { hasPermission } = useAuth();
    
    // 产品套装详情状态
    const [bundleDetail, setBundleDetail] = useState(null);
    const [bundleDetailLoading, setBundleDetailLoading] = useState(false);
    const [bundleDetailError, setBundleDetailError] = useState(null);
    
    // 产品套装项管理状态
    const [bundleItems, setBundleItems] = useState([]);
    const [products, setProducts] = useState([]);
    const [bundleItemsLoading, setBundleItemsLoading] = useState(false);
    const [productsLoading, setProductsLoading] = useState(false);
    
    // 产品套装项编辑抽屉状态
    const [itemDrawerVisible, setItemDrawerVisible] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [itemForm] = Form.useForm();
    const [itemFormLoading, setItemFormLoading] = useState(false);

    // 服务类型映射（复用 settlement STANDARD_COST_TYPE）
    const serviceTypeMap = SERVICE_TYPE_MAP;

    // 加载产品套装详情
    const loadBundleDetail = useCallback(async (bundleId) => {
        if (!bundleId) return;

        setBundleDetailLoading(true);
        setBundleDetailError(null);
        try {
            const response = await contractManagementService.productBundle.getProductBundleById(bundleId);
            setBundleDetail(response.data);
        } catch (error) {
            console.error("加载产品套装详情失败:", error);
            const errorMsg = error?.response?.data?.detail || error?.response?.data?.message || error?.message || "加载产品套装详情失败";
            setBundleDetailError(errorMsg);
            message.error(errorMsg);
        } finally {
            setBundleDetailLoading(false);
        }
    }, []);

    // 加载产品套装项列表
    const loadBundleItems = useCallback(async (bundleId) => {
        if (!bundleId) return;

        setBundleItemsLoading(true);
        try {
            const response = await contractManagementService.productBundle.getBundleItemList({
                bundle_id: bundleId,
                page_size: 500,
            });
            setBundleItems(response.data?.items || []);
        } catch (error) {
            console.error("加载产品套装项失败:", error);
            message.error("加载产品套装项失败");
        } finally {
            setBundleItemsLoading(false);
        }
    }, []);

    // 加载产品列表
    const loadProducts = useCallback(async (serviceType) => {
        if (!serviceType) return;
        
        setProductsLoading(true);
        try {
            const response = await contractManagementService.product.getProductList({
                service_type: serviceType,
                is_active: true,
                page_size: 500,
            });
            setProducts(response.data?.items || []);
        } catch (error) {
            console.error("加载产品列表失败:", error);
            message.error("加载产品列表失败");
        } finally {
            setProductsLoading(false);
        }
    }, []);

    // 当抽屉打开时加载数据
    useEffect(() => {
        if (visible && bundle?.id) {
            // 重置状态
            setBundleDetail(null);
            setBundleItems([]);
            setProducts([]);
            setBundleDetailError(null);
            
            // 加载产品套装详情
            loadBundleDetail(bundle.id);
            // 加载产品套装项（使用传入的bundle获取service_type，避免等待详情加载）
            loadBundleItems(bundle.id);
            // 加载产品列表
            if (bundle.service_type) {
                loadProducts(bundle.service_type);
            }
        }
    }, [visible, bundle?.id, bundle?.service_type, loadBundleDetail, loadBundleItems, loadProducts]);

    // 当抽屉关闭时重置状态
    useEffect(() => {
        if (!visible) {
            setBundleDetail(null);
            setBundleItems([]);
            setProducts([]);
            setBundleDetailError(null);
            setItemDrawerVisible(false);
            setEditingItem(null);
            itemForm.resetFields();
        }
    }, [visible, itemForm]);

    // 如果没有产品套装数据，不渲染组件
    if (!bundle) return null;

    // 使用详情数据（如果已加载）或回退到列表数据
    const displayBundle = bundleDetail || bundle;

    // 打开产品套装项编辑抽屉
    const openItemDrawer = (item = null) => {
        setEditingItem(item);
        if (item) {
            itemForm.setFieldsValue({
                product_id: item.product?.id,
                quantity: item.quantity,
            });
        } else {
            itemForm.resetFields();
            itemForm.setFieldsValue({
                quantity: 1,
            });
        }
        setItemDrawerVisible(true);
    };

    // 关闭产品套装项编辑抽屉
    const closeItemDrawer = () => {
        setItemDrawerVisible(false);
        setEditingItem(null);
        itemForm.resetFields();
    };

    // 保存产品套装项
    const saveBundleItem = async () => {
        try {
            setItemFormLoading(true);
            const values = await itemForm.validateFields();

            const processedValues = {
                ...values,
                bundle_id: bundle.id,
            };

            let response;
            if (editingItem) {
                // 更新
                response = await contractManagementService.productBundle.updateBundleItem(editingItem.id, processedValues);
                message.success("更新成功");
            } else {
                // 创建
                response = await contractManagementService.productBundle.createBundleItem(processedValues);
                message.success("添加成功");
            }

            closeItemDrawer();
            loadBundleItems(bundle.id);
            onSuccess?.();
        } catch (error) {
            console.error("保存失败:", error);
            const errorMsg = error?.response?.data?.detail || error?.response?.data?.message || error?.message || "保存失败";
            message.error(errorMsg);
        } finally {
            setItemFormLoading(false);
        }
    };

    // 删除产品套装项
    const deleteBundleItem = async (item) => {
        try {
            await contractManagementService.productBundle.deleteBundleItem(item.id, bundle.id);
            message.success("删除成功");
            loadBundleItems(bundle.id);
            onSuccess?.();
        } catch (error) {
            console.error("删除失败:", error);
            const errorMsg = error?.response?.data?.detail || error?.response?.data?.message || error?.message || "删除失败";
            message.error(errorMsg);
        }
    };

    // 产品套装项表格列定义
    const bundleItemColumns = [
        {
            title: "产品",
            dataIndex: ["product", "name"],
            key: "product_name",
            width: 200,
            ellipsis: true,
            render: (text, record) => (
                <div>
                    <div style={{ fontWeight: "500" }}>{record.product?.name || text || "-"}</div>
                    <Text type="secondary" style={{ fontSize: "12px" }}>
                        编码: {record.product?.code || "无"} | 单位: {record.product?.unit || "无"}
                    </Text>
                </div>
            ),
        },
        {
            title: "数量",
            dataIndex: "quantity",
            key: "quantity",
            width: 120,
            align: 'right',
            render: (quantity, record) => (
                <Text strong>
                    {quantity} {record.product?.unit || ""}
                </Text>
            ),
        },
        {
            title: "操作",
            key: "action",
            width: 120,
            align: 'center',
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    <PermissionGuard permission="contract:update">
                        <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => openItemDrawer(record)}
                        >
                            编辑
                        </Button>
                    </PermissionGuard>
                    <PermissionGuard permission="contract:delete">
                        <Popconfirm
                            title="确定删除这个产品套装项吗？"
                            onConfirm={() => deleteBundleItem(record)}
                            okText="确定"
                            cancelText="取消"
                        >
                            <Button
                                type="link"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                            >
                                删除
                            </Button>
                        </Popconfirm>
                    </PermissionGuard>
                </Space>
            ),
        },
    ];

    const serviceTypeInfo = serviceTypeMap[displayBundle.service_type] || { text: displayBundle.service_type, color: "default" };

    return (
        <>
            <Drawer
                title={
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div>
                            <Title level={4} style={{ margin: 0 }}>
                                产品套装详情
                            </Title>
                            <Text type="secondary" style={{ fontSize: "12px" }}>
                                {displayBundle.name || "未命名产品套装"}
                            </Text>
                        </div>
                        <Badge status={displayBundle.is_active ? "success" : "error"} text={displayBundle.is_active ? "启用" : "停用"} />
                    </div>
                }
                placement="right"
                width={1200}
                open={visible}
                onClose={onClose}
                extra={
                    <Space>
                        <PermissionGuard permission="contract:update">
                            <Button
                                type="primary"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => {
                                    onEdit(displayBundle);
                                    onClose();
                                }}
                            >
                                编辑产品套装
                            </Button>
                        </PermissionGuard>
                    </Space>
                }
            >
                <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    {/* 产品套装详情区域 */}
                    <Card 
                        title="产品套装基本信息" 
                        size="small" 
                        style={{ marginBottom: 16, flex: "0 0 auto" }}
                    >
                        {bundleDetailLoading ? (
                            <div style={{ textAlign: "center", padding: "20px 0" }}>
                                <Spin size="small" />
                                <Text type="secondary" style={{ marginLeft: 8 }}>正在加载产品套装详情...</Text>
                            </div>
                        ) : bundleDetailError ? (
                            <Alert
                                message="加载产品套装详情失败"
                                description={bundleDetailError}
                                type="error"
                                showIcon
                                style={{ marginBottom: 16 }}
                                action={
                                    <Button 
                                        size="small" 
                                        onClick={() => loadBundleDetail(bundle.id)}
                                    >
                                        重试
                                    </Button>
                                }
                            />
                        ) : (
                            <div style={{ display: "flex", gap: "24px" }}>
                                {/* 左列 */}
                                <div style={{ flex: 1 }}>
                                    <Descriptions size="small" column={1} labelStyle={{ width: "80px" }}>
                                        <Descriptions.Item label="产品套装名称">
                                            <Text strong>{displayBundle.name || "未命名产品套装"}</Text>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="产品套装编码">
                                            {displayBundle.bundle_code || "-"}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="服务类型">
                                            <Tag color={serviceTypeInfo.color}>{serviceTypeInfo.text}</Tag>
                                        </Descriptions.Item>
                                        <Descriptions.Item label="产品套装状态">
                                            <Badge status={displayBundle.is_active ? "success" : "error"} text={displayBundle.is_active ? "启用" : "停用"} />
                                        </Descriptions.Item>
                                    </Descriptions>
                                </div>
                                
                                {/* 右列 */}
                                <div style={{ flex: 1 }}>
                                    <Descriptions size="small" column={1} labelStyle={{ width: "80px" }}>
                                        <Descriptions.Item label="适用机场">
                                            {displayBundle.applicable_airports && displayBundle.applicable_airports.length > 0 ? (
                                                <Space size={4} wrap>
                                                    {displayBundle.applicable_airports.slice(0, 3).map((airport, index) => (
                                                        <Tag key={index} color="blue" size="small">
                                                            {airport}
                                                        </Tag>
                                                    ))}
                                                    {displayBundle.applicable_airports.length > 3 && (
                                                        <Tag color="default" size="small">
                                                            +{displayBundle.applicable_airports.length - 3}
                                                        </Tag>
                                                    )}
                                                </Space>
                                            ) : (
                                                <Text type="secondary">全部机场</Text>
                                            )}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="适用机型">
                                            {displayBundle.applicable_aircraft_types && displayBundle.applicable_aircraft_types.length > 0 ? (
                                                <Space size={4} wrap>
                                                    {displayBundle.applicable_aircraft_types.slice(0, 3).map((type, index) => (
                                                        <Tag key={index} color="green" size="small">
                                                            {type}
                                                        </Tag>
                                                    ))}
                                                    {displayBundle.applicable_aircraft_types.length > 3 && (
                                                        <Tag color="default" size="small">
                                                            +{displayBundle.applicable_aircraft_types.length - 3}
                                                        </Tag>
                                                    )}
                                                </Space>
                                            ) : (
                                                <Text type="secondary">全部机型</Text>
                                            )}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="创建时间">
                                            {formatDateTime(displayBundle.created_at)}
                                        </Descriptions.Item>
                                        <Descriptions.Item label="更新时间">
                                            {formatDateTime(displayBundle.updated_at)}
                                        </Descriptions.Item>
                                    </Descriptions>
                                </div>
                            </div>
                        )}

                        {/* 产品套装描述 */}
                        {displayBundle.description && (
                            <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #f0f0f0" }}>
                                <Text type="secondary" style={{ fontSize: "12px" }}>产品套装描述</Text>
                                <div style={{ marginTop: 4 }}>
                                    <Text>{displayBundle.description}</Text>
                                </div>
                            </div>
                        )}
                    </Card>

                    {/* 产品套装项管理区域 */}
                    <Card 
                        title={<span>产品套装项管理 ({bundleItems.length} 项)</span>}
                        size="small" 
                        style={{ flex: 1, display: "flex", flexDirection: "column", position: "relative" }}
                        bodyStyle={{ flex: 1, display: "flex", flexDirection: "column", padding: "16px 24px" }}
                    >
                        {/* 产品套装项表格 */}
                        <Table
                            dataSource={bundleItems}
                            columns={bundleItemColumns}
                            rowKey="id"
                            loading={bundleItemsLoading}
                            pagination={false}
                            size="small"
                            scroll={{ y: "calc(100vh - 400px)" }}
                            locale={{
                                emptyText: "暂无产品套装项",
                            }}
                        />

                        {/* 浮动新增按钮 */}
                        <PermissionGuard permission="contract:update">
                            <FloatButton
                                icon={<PlusOutlined />}
                                type="primary"
                                onClick={() => openItemDrawer()}
                                tooltip="新增产品套装项"
                                style={{
                                    position: 'absolute',
                                    bottom: 24,
                                    right: 24,
                                }}
                            />
                        </PermissionGuard>
                    </Card>
                </div>
            </Drawer>

            {/* 产品套装项编辑抽屉 */}
            <Drawer
                title={editingItem ? "编辑产品套装项" : "新增产品套装项"}
                placement="right"
                width={600}
                open={itemDrawerVisible}
                onClose={closeItemDrawer}
                maskClosable={false}
                extra={
                    <Space>
                        <Button onClick={closeItemDrawer}>
                            取消
                        </Button>
                        <Button 
                            type="primary" 
                            loading={itemFormLoading}
                            onClick={saveBundleItem}
                        >
                            保存
                        </Button>
                    </Space>
                }
            >
                <Form
                    form={itemForm}
                    layout="vertical"
                    style={{ height: '100%' }}
                >
                    <Form.Item
                        name="product_id"
                        label="产品"
                        rules={[{ required: true, message: "请选择产品" }]}
                    >
                        <Select
                            placeholder="选择产品"
                            loading={productsLoading}
                            showSearch
                            filterOption={(input, option) => 
                                option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {products.map((product) => (
                                <Option key={product.id} value={product.id}>
                                    {product.name} ({product.unit})
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="quantity"
                        label="数量"
                        rules={[
                            { required: true, message: "请输入数量" },
                            { type: "number", min: 1, message: "数量必须大于0" },
                        ]}
                    >
                        <InputNumber
                            placeholder="数量"
                            min={1}
                            style={{ width: "100%" }}
                        />
                    </Form.Item>
                </Form>
            </Drawer>
        </>
    );
};

export default ProductBundleDetailDrawer; 